import { WidgetState } from '@lumapps/widget-base/ducks/type';
import { isWidget } from '@lumapps/widget-base/types';
import { isWidgetOfValidType } from '@lumapps/widget-base/utils/isWidgetOfValidType';

import { isCell, isRow, Layout, LayoutOptions, WidgetReference } from '../types';
import { filterLayoutNodes } from './filterLayoutNodes';

/**
 * Format & filter widgets in layout API response.
 *
 * Widgets are filtered in the webview app to avoid showing widget not optimized for the hybrid app
 * (see ENABLED_WIDGET_TYPES in the WebviewContentPage)
 */
export function formatFilterLayoutWidgets(
    data: Layout,
    enabledWidgetTypes?: LayoutOptions['enabledWidgetTypes'],
): {
    layout: Layout;
    /** Widgets preloaded by the backend. Filtered for webview apps. */
    preloadedWidgets: WidgetState[];
    /** Widgets references to be loaded. We need them now to initialize their states in the store */
    widgetRefs: WidgetReference[];
} {
    const { widgets = [], ...layout } = data;
    const isWidgetEnabled = isWidgetOfValidType(enabledWidgetTypes);
    const shouldFilterEmptyRowsAndCells = Boolean(enabledWidgetTypes);

    // Filter and index widgets by id.
    const filteredPreloadedWidgets: Record<string, WidgetState> = {};
    for (const { widget } of widgets) {
        if (isWidgetEnabled(widget)) {
            filteredPreloadedWidgets[widget.widgetId] = widget;
        }
    }

    let filteredLayout = layout;
    const widgetRefs: WidgetReference[] = [];
    filteredLayout = filterLayoutNodes(
        filteredLayout,
        (node) => {
            // Storing widgets references that are not preloaded by the backend (and need to be fetched)
            if (isWidget(node) && !filteredPreloadedWidgets[node.widgetId]) {
                widgetRefs.push(node as WidgetReference);
            }

            // Keep rows, cells and valid widgets.
            if (isRow(node) || isCell(node) || isWidgetEnabled(node)) {
                return true;
            }
            // OR preloaded widget is valid.
            const widget = filteredPreloadedWidgets[(node as any).widgetId];
            return widget && isWidgetEnabled(widget);
        },
        // Remove empty cells & rows after filtering
        { removeEmptyNodes: shouldFilterEmptyRowsAndCells },
    );

    return { layout: filteredLayout, preloadedWidgets: Object.values(filteredPreloadedWidgets), widgetRefs };
}
